// pages/marketplace/PurchaseSuccess.jsx

import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { api } from '../../api/client';
import { CheckCircle, Mail, Download, Key, Package, ArrowRight, Loader, AlertCircle, Sparkles } from 'lucide-react';
import MarketplaceLayout from '../../layouts/MarketplaceLayout';

export default function PurchaseSuccess() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [purchase, setPurchase] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    
    const sessionId = searchParams.get('session_id');
    const isNewAccount = searchParams.get('new_account') === 'true';
    
    useEffect(() => {
        if (sessionId) {
            // Give webhook time to process
            setTimeout(() => {
                fetchPurchaseDetails();
            }, 2000);
        } else {
            setError('Invalid purchase session');
            setLoading(false);
        }
    }, [sessionId]);
    
    const fetchPurchaseDetails = async () => {
        try {
            const response = await api.get(`/api/stripe/purchase/${sessionId}`);
            setPurchase(response.data);
            setError('');
        } catch (err) {
            console.error('Failed to fetch purchase details:', err);
            // Try once more after delay
            setTimeout(async () => {
                try {
                    const response = await api.get(`/api/stripe/purchase/${sessionId}`);
                    setPurchase(response.data);
                    setError('');
                } catch (retryErr) {
                    setError('Your purchase was successful! Check your email for license details.');
                }
            }, 3000);
        } finally {
            setLoading(false);
        }
    };
    
    const copyLicenseKey = () => {
        if (purchase?.license_key) {
            navigator.clipboard.writeText(purchase.license_key);
            
            // Show toast notification
            const toast = document.createElement('div');
            toast.className = 'fixed bottom-4 right-4 bg-green-600 text-white px-4 py-2 rounded-lg shadow-lg z-50';
            toast.textContent = 'License key copied!';
            document.body.appendChild(toast);
            setTimeout(() => toast.remove(), 3000);
        }
    };
    
    if (loading) {
        return (
            <MarketplaceLayout>
                <div className="min-h-screen flex items-center justify-center">
                    <div className="text-center">
                        <Loader className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
                        <p className="text-gray-600">Processing your purchase...</p>
                    </div>
                </div>
            </MarketplaceLayout>
        );
    }
    
    return (
        <MarketplaceLayout>
            <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
                <div className="max-w-3xl mx-auto px-4 py-16">
                    {/* Success Animation */}
                    <div className="text-center mb-8">
                        <div className="relative inline-block">
                            <div className="absolute inset-0 animate-ping">
                                <div className="w-24 h-24 bg-green-400 rounded-full opacity-20"></div>
                            </div>
                            <div className="relative inline-flex items-center justify-center w-24 h-24 bg-green-500 rounded-full mb-4">
                                <CheckCircle className="w-14 h-14 text-white" />
                            </div>
                        </div>
                        
                        <h1 className="text-4xl font-bold text-gray-900 mb-3">
                            🎉 Congratulations!
                        </h1>
                        <p className="text-xl text-gray-600">
                            Your purchase was successful
                        </p>
                    </div>
                    
                    {/* New Account Welcome */}
                    {isNewAccount && (
                        <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl p-6 mb-8 shadow-lg">
                            <div className="flex items-center gap-3 mb-3">
                                <Sparkles className="w-6 h-6" />
                                <h3 className="text-xl font-bold">Welcome to Dentulu Marketplace!</h3>
                            </div>
                            <p className="mb-2">
                                We've created your account and sent you a welcome email with:
                            </p>
                            <ul className="list-disc list-inside space-y-1 ml-2">
                                <li>Your login credentials</li>
                                <li>Instructions to set your password</li>
                                <li>Your license key and activation guide</li>
                            </ul>
                        </div>
                    )}
                    
                    {/* Purchase Details or Email Notice */}
                    {error ? (
                        <div className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
                            <div className="bg-gradient-to-r from-green-500 to-green-600 p-1"></div>
                            <div className="p-8 text-center">
                                <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                                    <Mail className="w-8 h-8 text-green-600" />
                                </div>
                                <h2 className="text-2xl font-bold mb-3">Payment Successful!</h2>
                                <p className="text-gray-600 mb-6">{error}</p>
                                <div className="bg-gray-50 rounded-lg p-4">
                                    <p className="text-sm text-gray-700">
                                        <strong>What happens next?</strong><br />
                                        You'll receive an email within 24 to 48 hours containing your license key 
                                        and detailed activation instructions.
                                    </p>
                                </div>
                            </div>
                        </div>
                    ) : purchase && (
                        <div className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
                            <div className="bg-gradient-to-r from-blue-500 to-blue-600 p-1"></div>
                            <div className="p-8">
                                <h2 className="text-2xl font-bold mb-6">Order Details</h2>
                                
                                {/* App Info */}
                                <div className="flex items-start gap-4 mb-8 pb-8 border-b">
                                    {purchase.app.icon_url ? (
                                        <img 
                                            src={purchase.app.icon_url} 
                                            alt={purchase.app.name} 
                                            className="w-20 h-20 rounded-xl shadow-md"
                                        />
                                    ) : (
                                        <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-md">
                                            <Package className="w-10 h-10 text-white" />
                                        </div>
                                    )}
                                    <div className="flex-1">
                                        <h3 className="text-xl font-semibold mb-2">{purchase.app.name}</h3>
                                        <div className="flex items-center gap-4 text-gray-600">
                                            <span className="font-semibold text-2xl text-gray-900">
                                                ${purchase.amount} {purchase.currency}
                                            </span>
                                            <span className="text-green-600 font-medium">✓ Paid</span>
                                        </div>
                                    </div>
                                </div>
                                
                                {/* License Key
                                <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl p-6 mb-8">
                                    <div className="flex items-start gap-3">
                                        <div className="p-2 bg-white rounded-lg shadow-sm">
                                            <Key className="w-6 h-6 text-gray-700" />
                                        </div>
                                        <div className="flex-1">
                                            <h4 className="font-semibold mb-3 text-lg">Your License Key</h4>
                                            <div className="flex items-center gap-3 mb-3">
                                                <code className="flex-1 bg-white px-4 py-3 rounded-lg border-2 border-gray-200 text-sm font-mono text-center shadow-sm">
                                                    {purchase.license_key}
                                                </code>
                                                <button
                                                    onClick={copyLicenseKey}
                                                    className="px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
                                                >
                                                    Copy
                                                </button>
                                            </div>
                                            <p className="text-sm text-gray-600">
                                                ⚡ Keep this key safe - you'll need it to activate your software
                                            </p>
                                        </div>
                                    </div>
                                </div> */}
                                
                                {/* Next Steps */}
                                <div>
                                    <h4 className="font-semibold mb-4 text-lg">Next Steps</h4>
                                    <div className="space-y-4">
                                        <div className="flex gap-4">
                                            <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                                                <span className="text-blue-600 font-semibold">1</span>
                                            </div>
                                            <div>
                                                <p className="font-medium mb-1">Check your email</p>
                                                <p className="text-sm text-gray-600">
                                                    We've sent detailed instructions to your email address
                                                </p>
                                            </div>
                                        </div>
                                        
                                        <div className="flex gap-4">
                                            <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                                                <span className="text-blue-600 font-semibold">2</span>
                                            </div>
                                            <div>
                                                <p className="font-medium mb-1">Download the software</p>
                                                <p className="text-sm text-gray-600">
                                                    Visit the developer's website to download the application
                                                </p>
                                            </div>
                                        </div>
                                        
                                        <div className="flex gap-4">
                                            <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                                                <span className="text-blue-600 font-semibold">3</span>
                                            </div>
                                            <div>
                                                <p className="font-medium mb-1">Activate your license</p>
                                                <p className="text-sm text-gray-600">
                                                    Enter your license key when prompted during setup
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            
                            {/* Action Buttons */}
                            <div className="bg-gray-50 px-8 py-6 flex items-center justify-between">
                                <Link
                                    to="/marketplace/my-purchases"
                                    className="text-blue-600 hover:text-blue-700 font-medium flex items-center gap-2"
                                >
                                    View all purchases
                                    <ArrowRight className="w-4 h-4" />
                                </Link>
                                <Link
                                    to={`/marketplace/app/${purchase.app.slug}`}
                                    className="bg-blue-600 text-white px-6 py-2.5 rounded-lg hover:bg-blue-700 transition-colors font-medium"
                                >
                                    View App Details
                                </Link>
                            </div>
                        </div>
                    )}
                    
                    {/* Support Section */}
                    <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl p-8 text-center">
                        <h3 className="text-xl font-semibold mb-3">Need Help?</h3>
                        <p className="text-gray-600 mb-6 max-w-lg mx-auto">
                            If you have any questions about your purchase or need help with activation, 
                            our support team is here to assist you.
                        </p>
                        <div className="flex justify-center gap-4">
                            <Link
                                to="/support"
                                className="inline-flex items-center gap-2 bg-white px-6 py-2.5 rounded-lg hover:bg-gray-50 transition-colors border border-gray-200"
                            >
                                <Mail className="w-5 h-5" />
                                Contact Support
                            </Link>
                            <Link
                                to="/docs"
                                className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium"
                            >
                                View Documentation
                                <ArrowRight className="w-4 h-4" />
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        </MarketplaceLayout>
    );
}